import { SUBJECT_VIEW_MODES, VIEW_MODES_CONFIG } from "@components/view-mode-selector/view-mode-selector-constants";
import { ViewModeSelectorItem } from "@components/view-mode-selector/view-mode-selector-item";
import { neutral } from "@flat-ui";
import { Box } from "@mui/material";
import { appStateSelector } from "@store/app/app-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { dataViewModeSelector } from "@store/ui/ui-selector";
import { setDataViewMode } from "@store/ui/ui-slice";
import { DataViewMode, DataViewModeSubject } from "@store/ui/ui-slice-types";
import { DataViewEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";

interface Props {
  /** The type of the subject that the data is going to be displayed */
  subject: DataViewModeSubject;
}

/**
 * Selector to choose the data view mode for a given subject (groups, projects, etc.)
 */
export function ViewModeSelector({ subject }: Props): JSX.Element {
  const { trackEvent } = useTrackEvent();
  const dispatch = useAppDispatch();
  const { isAlphaTestingEnabled } = useAppSelector(appStateSelector);
  const subjectsViewMode = useAppSelector(dataViewModeSelector);
  const currentViewMode = subjectsViewMode[subject];
  const viewModes = SUBJECT_VIEW_MODES[subject];

  // TODO: Hide map view mode behind alpha flag until it's ready for production
  // https://faro01.atlassian.net/browse/ST-3944
  const visibleViewModes = viewModes.filter((viewMode) => {
    return isAlphaTestingEnabled || viewMode !== "map";
  });

  /**
   * Updates the data view mode for the current subject and tracks the event.
   */
  function updateDataViewMode(viewMode: DataViewMode): void {
    trackEvent({
      name: DataViewEvents.changeViewMode,
      props: {
        viewMode,
      },
    });

    dispatch(setDataViewMode({
      subject,
      viewMode,
    }));
  }

  return (
    <Box
      component="div"
      sx={{
        backgroundColor: neutral[100],
        borderRadius: "4px",
        height: "36px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        paddingX: "3px",
        marginRight: "12px",
      }}
    >
      {visibleViewModes.map((viewMode) =>
        <ViewModeSelectorItem
          key={viewMode}
          tooltipTitle={VIEW_MODES_CONFIG[viewMode].tooltipTitle}
          component={VIEW_MODES_CONFIG[viewMode].iconComponent}
          isActive={currentViewMode === viewMode}
          onClick={() => updateDataViewMode(viewMode)}
        />
      )}
    </Box>
  );
}
